<script setup>
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { useIntegrationHook } from 'dashboard/composables/useIntegrationHook';
import { useBranding } from 'shared/composables/useBranding';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

const props = defineProps({
  integrationId: {
    type: String,
    required: true,
  },
});

defineEmits(['add', 'delete']);

const store = useStore();

const { integration, hasConnectedHooks } = useIntegrationHook(
  props.integrationId
);

const { replaceInstallationName } = useBranding();

const isUpdatingLabelSuggestion = ref(false);
const isOpenAIIntegration = computed(() => props.integrationId === 'openai');
const connectedHook = computed(() => integration.value?.hooks?.[0] || null);
const isLabelSuggestionEnabled = computed(
  () => !!connectedHook.value?.settings?.label_suggestion
);

const updateLabelSuggestion = async value => {
  if (!connectedHook.value) {
    return;
  }

  isUpdatingLabelSuggestion.value = true;
  try {
    await store.dispatch('integrations/updateHook', {
      hookId: connectedHook.value.id,
      hookData: {
        hook: {
          settings: {
            label_suggestion: value,
          },
        },
      },
    });
    useAlert(
      value
        ? 'Sugestao de tags ativada com sucesso.'
        : 'Sugestao de tags desativada com sucesso.'
    );
  } catch (error) {
    useAlert('Nao foi possivel atualizar a sugestao de tags.');
  } finally {
    isUpdatingLabelSuggestion.value = false;
  }
};
</script>

<template>
  <div
    class="outline outline-n-container outline-1 bg-n-card rounded-xl flex-grow overflow-auto p-4"
  >
    <div class="flex items-center justify-center">
      <div class="flex h-16 w-16 items-center justify-center">
        <img
          :src="`/dashboard/images/integrations/${integrationId}.png`"
          class="max-w-full rounded-md border border-n-weak shadow-sm block dark:hidden bg-n-alpha-3 dark:bg-n-alpha-2"
        />
        <img
          :src="`/dashboard/images/integrations/${integrationId}-dark.png`"
          class="max-w-full rounded-md border border-n-weak shadow-sm hidden dark:block bg-n-alpha-3 dark:bg-n-alpha-2"
        />
      </div>
      <div class="flex flex-col justify-center m-0 mx-4 flex-1">
        <h3 class="mb-1 text-heading-1 text-n-slate-12">
          {{ integration.name }}
        </h3>
        <p class="text-n-slate-11 text-body-main">
          {{ replaceInstallationName(integration.description) }}
        </p>
        <div
          v-if="hasConnectedHooks && isOpenAIIntegration"
          class="mt-3 flex items-center gap-2"
        >
          <Switch
            :model-value="isLabelSuggestionEnabled"
            :disabled="isUpdatingLabelSuggestion"
            @update:model-value="updateLabelSuggestion"
          />
          <span class="text-body-main text-n-slate-12">
            {{ $t('CAPTAIN_SETTINGS.FEATURES.LABEL_SUGGESTION.TITLE') }}
          </span>
        </div>
      </div>
      <div class="flex justify-center items-center mb-0 w-[15%]">
        <div v-if="hasConnectedHooks">
          <div @click="$emit('delete', integration.hooks[0])">
            <Button
              ruby
              faded
              :label="$t('INTEGRATION_APPS.DISCONNECT.BUTTON_TEXT')"
            />
          </div>
        </div>
        <div v-else>
          <Button
            blue
            faded
            :label="$t('INTEGRATION_APPS.CONNECT.BUTTON_TEXT')"
            @click="$emit('add')"
          />
        </div>
      </div>
    </div>
  </div>
</template>
